import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { Project, ProjectCost, CostSummary } from '../types';

const typeLabels: Record<string, string> = {
  MATERIAL: 'Material', LABOR: 'Lohn', EXTERNAL: 'Fremdleistung', OTHER: 'Sonstiges', LABOR_TIME: 'Arbeitszeit',
};
const typeColors: Record<string, string> = {
  MATERIAL: 'bg-blue-50 text-blue-700 border-blue-200',
  LABOR: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  EXTERNAL: 'bg-purple-50 text-purple-700 border-purple-200',
  OTHER: 'bg-slate-50 text-slate-700 border-slate-200',
  LABOR_TIME: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function CostsPage() {
  const qc = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [adding, setAdding] = useState<Partial<ProjectCost> | null>(null);

  const { data: overview } = useQuery<{ projects: any[]; totals: any }>({
    queryKey: ['costs-overview'],
    queryFn: () => api.get('/costs/overview').then((r) => r.data),
  });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((r) => r.data),
  });

  const { data: summary } = useQuery<CostSummary>({
    queryKey: ['costs-summary', selectedProject],
    queryFn: () => api.get(`/costs/summary/${selectedProject}`).then((r) => r.data),
    enabled: !!selectedProject,
  });

  const { data: costs = [] } = useQuery<ProjectCost[]>({
    queryKey: ['costs', selectedProject],
    queryFn: () => api.get(`/costs?projectId=${selectedProject}`).then((r) => r.data),
    enabled: !!selectedProject,
  });

  const saveMutation = useMutation({
    mutationFn: (c: Partial<ProjectCost>) => api.post('/costs', c),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['costs'] });
      qc.invalidateQueries({ queryKey: ['costs-summary'] });
      qc.invalidateQueries({ queryKey: ['costs-overview'] });
      setAdding(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/costs/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['costs'] });
      qc.invalidateQueries({ queryKey: ['costs-summary'] });
      qc.invalidateQueries({ queryKey: ['costs-overview'] });
    },
  });

  const fmt = (n: number) => Number(n).toFixed(2).replace('.', ',');

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Nachkalkulation</h1>
          <p className="sp-page-subtitle">Soll-Ist-Vergleich und Projektkosten-Controlling</p>
        </div>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="sp-stat-card">
            <span className="sp-stat-label">Geplant (netto)</span>
            <span className="sp-stat-value">{fmt(overview.totals.planned)} EUR</span>
          </div>
          <div className="sp-stat-card">
            <span className="sp-stat-label">Fakturiert</span>
            <span className="sp-stat-value">{fmt(overview.totals.invoiced)} EUR</span>
          </div>
          <div className="sp-stat-card">
            <span className="sp-stat-label">Bezahlt</span>
            <span className="sp-stat-value text-emerald-600">{fmt(overview.totals.paid)} EUR</span>
          </div>
          <div className="sp-stat-card">
            <span className="sp-stat-label">Gewinn/Verlust</span>
            <span className={`sp-stat-value ${overview.totals.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {overview.totals.profit >= 0 ? '+' : ''}{fmt(overview.totals.profit)} EUR
            </span>
          </div>
        </div>
      )}

      {/* Projects Overview Table */}
      {overview && overview.projects.length > 0 && (
        <div className="sp-card overflow-hidden mb-6 animate-fade-in">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Projektübersicht</h3>
          </div>
          <table className="sp-table">
            <thead>
              <tr>
                <th>Projekt</th>
                <th>Kunde</th>
                <th className="text-right">Geplant</th>
                <th className="text-right">Kosten</th>
                <th className="text-right">Gewinn</th>
                <th className="text-right">Marge</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {overview.projects.map((p: any) => (
                <tr key={p.id} className={selectedProject === p.id ? 'bg-brand-50' : ''}>
                  <td className="font-medium text-slate-800">{p.title}</td>
                  <td className="text-slate-500">{p.customer}</td>
                  <td className="text-right font-mono text-xs">{fmt(p.planned)} EUR</td>
                  <td className="text-right font-mono text-xs">{fmt(p.costs)} EUR</td>
                  <td className={`text-right font-mono text-xs font-semibold ${p.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {p.profit >= 0 ? '+' : ''}{fmt(p.profit)} EUR
                  </td>
                  <td className="text-right">
                    <span className={`sp-badge border ${p.margin >= 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                      {p.margin}%
                    </span>
                  </td>
                  <td>
                    <button onClick={() => setSelectedProject(p.id)} className="sp-btn-icon" title="Details">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Project Detail */}
      {selectedProject && summary && (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800">{summary.projectTitle} — Detailkalkulation</h2>
            <button onClick={() => setAdding({ projectId: selectedProject, type: 'MATERIAL', amount: 0, description: '' })} className="sp-btn-primary">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Kosten erfassen
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="sp-card p-5">
              <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Soll (Angebote)</div>
              <div className="text-xl font-bold text-slate-800">{fmt(summary.planned.net)} EUR</div>
            </div>
            <div className="sp-card p-5">
              <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Ist (Kosten)</div>
              <div className="text-xl font-bold text-slate-800">{fmt(summary.costs.total)} EUR</div>
              <div className="text-xs text-slate-400 mt-1">
                davon {summary.time.totalHours}h Arbeitszeit ({fmt(summary.time.laborCost)} EUR)
              </div>
            </div>
            <div className="sp-card p-5">
              <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Ergebnis</div>
              <div className={`text-xl font-bold ${summary.result.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {summary.result.profit >= 0 ? '+' : ''}{fmt(summary.result.profit)} EUR
              </div>
              <div className="text-xs text-slate-400 mt-1">Marge: {summary.result.margin}%</div>
            </div>
          </div>

          {/* Cost Breakdown by Type */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {Object.entries(summary.costs.byType).map(([type, amount]) => (
              <div key={type} className="sp-card p-4">
                <span className={`sp-badge border text-[10px] mb-2 ${typeColors[type] || typeColors.OTHER}`}>
                  {typeLabels[type] || type}
                </span>
                <div className="text-sm font-bold text-slate-800">{fmt(amount as number)} EUR</div>
              </div>
            ))}
          </div>

          {/* Add Cost Form */}
          {adding && (
            <div className="sp-form-card mb-4">
              <h3 className="sp-form-title">Kosten erfassen</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="sp-label">Kostenart</label>
                  <select value={adding.type || 'MATERIAL'} onChange={(e) => setAdding({ ...adding, type: e.target.value as any })} className="sp-input">
                    <option value="MATERIAL">Material</option>
                    <option value="LABOR">Lohn</option>
                    <option value="EXTERNAL">Fremdleistung</option>
                    <option value="OTHER">Sonstiges</option>
                  </select>
                </div>
                <div>
                  <label className="sp-label">Betrag (EUR)</label>
                  <input type="number" step="0.01" value={adding.amount ?? 0} onChange={(e) => setAdding({ ...adding, amount: +e.target.value })} className="sp-input" />
                </div>
                <div>
                  <label className="sp-label">Lieferant</label>
                  <input value={adding.supplier || ''} onChange={(e) => setAdding({ ...adding, supplier: e.target.value })} className="sp-input" placeholder="optional" />
                </div>
                <div className="col-span-2">
                  <label className="sp-label">Beschreibung</label>
                  <input value={adding.description || ''} onChange={(e) => setAdding({ ...adding, description: e.target.value })} className="sp-input" placeholder="z.B. Elektromaterial Baumarkt" autoFocus />
                </div>
                <div>
                  <label className="sp-label">Rechnungsnr.</label>
                  <input value={adding.invoiceRef || ''} onChange={(e) => setAdding({ ...adding, invoiceRef: e.target.value })} className="sp-input" placeholder="optional" />
                </div>
              </div>
              <div className="sp-form-actions">
                <button onClick={() => saveMutation.mutate(adding)} disabled={saveMutation.isPending} className="sp-btn-primary">
                  {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
                </button>
                <button onClick={() => setAdding(null)} className="sp-btn-secondary">Abbrechen</button>
              </div>
            </div>
          )}

          {/* Costs Table */}
          <div className="sp-card overflow-hidden">
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Art</th>
                  <th>Beschreibung</th>
                  <th>Lieferant</th>
                  <th className="text-right">Betrag</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {costs.length === 0 ? (
                  <tr><td colSpan={6}><div className="sp-empty">
                    <span className="text-sm font-medium">Keine Kosten erfasst</span>
                  </div></td></tr>
                ) : costs.map((c) => (
                  <tr key={c.id}>
                    <td className="text-xs text-slate-500">{new Date(c.date).toLocaleDateString('de-DE')}</td>
                    <td><span className={`sp-badge border text-[10px] ${typeColors[c.type] || typeColors.OTHER}`}>{typeLabels[c.type]}</span></td>
                    <td className="text-slate-800">{c.description}</td>
                    <td className="text-slate-500">{c.supplier || '-'}</td>
                    <td className="text-right font-mono text-xs font-semibold">{fmt(Number(c.amount))} EUR</td>
                    <td className="text-right">
                      <button onClick={() => { if (confirm('Eintrag löschen?')) deleteMutation.mutate(c.id); }}
                        className="sp-btn-icon hover:!text-error-500 hover:!bg-error-50" title="Löschen">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
